import { REVIEW_LENSES } from '../../api/structs/pipeline';
import { validateAgentCheckpoint } from './validateAgentCheckpoint';
import { validateOutputContract, validateOutputFields, validateToolCheckpoint } from './validateToolCheckpoint';

/**
 * A single client-side pre-validation finding for a pipeline template.
 * `checkpointId` is null when the issue isn't tied to a specific checkpoint id
 * (e.g. a checkpoint that has no id at all, or a pipeline-level output_format issue).
 */
export interface PipelineValidationIssue {
  level: 'error' | 'warning';
  checkpointId: string | null;
  message: string;
  /**
   * Set only for the findings a CALLER has to treat specially, because the Go
   * save paths do:
   *  - `output_format`: completeness is enforced strictly on create (and on the
   *    builder's dry run) but leniently on update, so the manager downgrades it.
   *  - `image_to_image_reference`: the update path grandfathers a violation the
   *    stored template already had, so an unrelated edit isn't blocked by legacy data.
   *  - `undeclared_parameter`: a warning everywhere (declaring a parameter is
   *    optional) — tagged so the editors can point at their inline hint.
   */
  code?: PipelineValidationCode;
}

export type PipelineValidationCode =
  | 'output_format'
  | 'image_to_image_reference'
  | 'undeclared_parameter'
  | 'profile_seed_image';

/**
 * Codes the backend's update path grandfathers when the STORED template already
 * had the violation and this edit does not touch the offending wiring — both are
 * "your legacy template already fails at run time" lints, and neither may make
 * an old pipeline unsavable for an unrelated edit like a rename. Server side:
 * `WithGrandfatheredImageToImage` and the grandfathered set passed to
 * `ValidateProfileGeneratorAssets` (updatePipelineTemplate.go).
 */
export const GRANDFATHERABLE_CODES: ReadonlySet<PipelineValidationCode> = new Set<PipelineValidationCode>([
  'image_to_image_reference',
  'profile_seed_image',
]);

/**
 * The shape this validator actually reads. Deliberately structural and loose so
 * BOTH callers fit without a cast: a builder-emitted `ParsedPipelineTemplate`
 * (raw LLM JSON) and a stored `PipelineTemplate` from the manager.
 */
export interface ValidatablePipeline {
  output_format?: unknown;
  /** Run-level parameter declarations — `{ scene_count: { default: "9" } }`. */
  parameters?: unknown;
  checkpoints?: unknown;
  /** Template-level review capability/model defaults (Go types.ReviewDefaults). */
  review_defaults?: unknown;
}

/**
 * An emitted prompt template reference. Either just an id (legacy — content-based
 * rules are skipped when only an id is known) or an `{id, content}` pair (e.g. a
 * `ParsedPromptTemplate`), which unlocks the generator-template-fitness and
 * clipStyle-repertoire checks below.
 */
export type EmittedPromptTemplateRef = string | { id: string; content?: string };

/**
 * A prompt template already known to exist on the server (e.g. from
 * `PipelineAPI.listPromptTemplates()`), so the "wasn't emitted in this message"
 * warning can be suppressed for ids that are real but simply weren't re-emitted.
 * `content` is optional — when present it also unlocks the generator-template-fitness
 * check against a server-side template a generator was mistakenly pointed at.
 */
export type KnownServerPromptTemplate = { id: string; content?: string };

/**
 * What a generator profile needs as INPUT, as published by the model registry
 * inside a profile catalog entry's `profile` block
 * (contentgen-model-registry/app/data/profile_models.py). Derived from the
 * profile spec's `params.mode`, which is what the backend runtime dispatches on.
 */
export interface ProfileCompatibility {
  /** 'txt2img' | 'img2img' | 'inpaint' — already normalized by the registry. */
  generationMode: string;
  /** img2img and inpaint both consume a seed image attachment. */
  requiresSeedImage: boolean;
  /** inpaint additionally consumes an attachment with role "mask". */
  requiresMask: boolean;
}

/**
 * Reads the compatibility metadata off a catalog model entry. Returns null when
 * the entry carries none — an older registry that predates the fields — so the
 * caller can degrade to a warning instead of guessing.
 */
export function readProfileCompatibility(model: unknown): ProfileCompatibility | null {
  const profile = asRecord(asRecord(model).profile);
  const generationMode = asString(profile.generation_mode).trim().toLowerCase();
  if (!generationMode) return null;
  return {
    generationMode,
    requiresSeedImage: profile.requires_seed_image === true,
    requiresMask: profile.requires_mask === true,
  };
}

/**
 * True when a checkpoint declares a source that can actually deliver an
 * attachment to its generator at run time: a required_assets entry, or an
 * input_mapping "attachments:<checkpoint_id>" source. Mirrors the Go
 * resolveGeneratorAttachments chain (generator_checkpoint.go) — and, unlike the
 * image_to_image rule, deliberately EXCLUDES an output_spec literal, which the
 * profile generator never reads (it resolves its seed only from attachments).
 *
 * Exported so the model picker can flag an incompatible profile with exactly the
 * predicate the validator will judge the save by.
 */
export function checkpointDeclaresAttachmentSource(cp: unknown): boolean {
  const checkpoint = asRecord(cp);
  if (asArray(checkpoint.required_assets).length > 0) return true;
  return Object.values(asRecord(checkpoint.input_mapping)).some(
    (value) => typeof value === 'string' && value.trim().startsWith('attachments:')
  );
}

/**
 * True when a checkpoint's declarations name a mask anywhere: a required_assets
 * entry keyed/typed "mask", or an "attachments:<id>?role=mask" (or "#mask")
 * selector. A mask reaches the runtime by attachment ROLE, so this is evidence
 * of intent, never proof — the validator only warns on its absence.
 */
export function checkpointDeclaresMaskSource(cp: unknown): boolean {
  const checkpoint = asRecord(cp);
  const declaresMaskAsset = asArray(checkpoint.required_assets).some((raw) => {
    const asset = asRecord(raw);
    return asString(asset.key).trim().toLowerCase() === 'mask' || asString(asset.type).trim().toLowerCase() === 'mask';
  });
  if (declaresMaskAsset) return true;
  return Object.values(asRecord(checkpoint.input_mapping)).some((value) => {
    if (typeof value !== 'string') return false;
    const source = value.trim();
    if (!source.startsWith('attachments:')) return false;
    return /[?&]role=mask(&|$)/i.test(source) || /#mask$/i.test(source);
  });
}

/**
 * The attachment sources a checkpoint declares, as the model picker needs them.
 * Derived with the very predicates the validator judges a save by, so the two
 * can never disagree about WHAT A CHECKPOINT SUPPLIES. (They do differ on the
 * metadata-absent path on purpose: the validator warns, the picker shows no
 * chip — see profileCompatibilityGaps.)
 */
export interface CheckpointAssetContext {
  hasImageSource: boolean;
  hasMaskSource: boolean;
}

/** Reads {@link CheckpointAssetContext} off a checkpoint object. */
export function readCheckpointAssetContext(cp: unknown): CheckpointAssetContext {
  return {
    hasImageSource: checkpointDeclaresAttachmentSource(cp),
    hasMaskSource: checkpointDeclaresMaskSource(cp),
  };
}

const CHECKPOINT_TYPES = new Set(['prompt', 'distributor', 'sequencer', 'connector', 'generator', 'upload', 'tool', 'agent']);
/** Checkpoint types whose `review` block the engine honours (validation.go). */
const REVIEWABLE_TYPES = new Set(['prompt', 'generator']);
/** The one alternative to `checkpoint:<id>` in a sequencer's allowed_parent_refs. */
const USER_REF_SOURCE = 'user';
const MEDIA_TYPES = new Set(['image', 'video', 'audio']);
const CONNECTOR_STRATEGIES = new Set(['collect_all', 'collect_lora_dataset']);

/** Provider allow-list per generator media type — mirrors the Go backend's isGeneratorSupportedForMediaType. */
const PROVIDER_ALLOW_LIST: Record<string, string[]> = {
  image: ['openrouter', 'runware', 'vertexai', 'profile'],
  video: ['runware', 'vertexai'],
  audio: ['suno', 'runware'],
};

/** The one built-in prompt template the client can vouch for without a store lookup. */
const KNOWN_BUILTIN_PROMPT_TEMPLATE_IDS = new Set(['generator-pass-through-prompt']);
/** The pass-through built-in is equivalent to this literal template content. */
const PASS_THROUGH_TEMPLATE_CONTENT = '{{prompt}}';

/** Recommended output_format defaults per media type, referenced in "completely absent" messages. */
const RECOMMENDED_OUTPUT_FORMAT_DEFAULTS: Record<string, { provider: string; model: string }> = {
  image: { provider: 'vertexai', model: 'gemini-3.1-flash-image-preview' },
  video: { provider: 'vertexai', model: 'veo-3.1-lite-generate-001' },
  audio: { provider: 'suno', model: 'V5' },
};

/** Assembly-template markers that should never appear in a generator's own prompt template. */
const ASSEMBLY_TEMPLATE_MARKERS = ['aiVideoPrompts', 'imagePrompts', 'clipStyle'];

/** ClipStyle values the renderer actually supports. */
const SUPPORTED_CLIP_STYLES = new Set(['reel', 'quote', 'genericCarousel']);

/** The one supported required_assets wiring: which prior checkpoint provides the asset. */
const CHECKPOINT_SOURCE_PREFIX = 'checkpoint:';

/**
 * output_spec keys that carry a literal seed/reference image — one of the three
 * ways an image_to_image generator can legitimately declare a reference source
 * (mirrors outputSpecDeclaresReferenceInput in validation.go).
 */
const OUTPUT_SPEC_REFERENCE_KEYS = ['seed_image', 'seed_images', 'seedImages', 'reference_frames', 'referenceFrames'];

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asRecord = (value: unknown): Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** Human-readable label for a checkpoint used inside messages: its id, or a positional fallback. */
const checkpointLabel = (id: string, index: number): string => id || `checkpoint at position ${index + 1}`;

/**
 * Validates a builder-emitted pipeline against the same rules the Go backend
 * enforces at save time (see internal/pipeline/validation.go), so obviously-broken
 * pipelines can be flagged before the round trip to the server. Never throws —
 * every field access tolerates missing/malformed data, since the pipeline comes
 * from LLM-emitted JSON that may be incomplete mid-conversation.
 *
 * @param emittedPromptTemplates prompt templates emitted as ```prompt-template
 *   blocks in the same message. Accepts either bare ids (legacy — content-based
 *   rules are skipped) or `{id, content}` pairs, which additionally unlocks:
 *   generator-template-fitness checks (placeholder/input_mapping consistency,
 *   assembly-template misuse) and the clipStyle-repertoire check. A
 *   prompt/distributor/generator checkpoint referencing a prompt_template_id
 *   outside this set (and not a known built-in, and not in `knownServerTemplates`)
 *   is a WARNING, not an error — the client can't see the server's full built-in
 *   template list.
 * @param knownServerTemplates prompt templates already known to exist on the
 *   server (e.g. `PipelineAPI.listPromptTemplates()`), so a checkpoint referencing
 *   a real server-side template that just wasn't re-emitted in this message
 *   doesn't false-positive as "wasn't emitted". Optional — omit (or pass `[]`) to
 *   preserve prior behavior (every non-emitted, non-builtin id warns). When a
 *   server template's content is known, it also unlocks the generator-template-
 *   fitness check against it (e.g. catching a generator pointed at a server-side
 *   assembly template).
 * @param profileModels generator-profile catalog entries (provider "profile")
 *   as served by the registry, used to check that a checkpoint pointed at an
 *   img2img/inpaint profile can actually supply the seed image (and mask) that
 *   profile needs. Optional: an empty list skips the check entirely, and a
 *   listed model whose entry carries no compatibility metadata (older registry)
 *   downgrades to a warning — the rule never guesses.
 */
export function validatePipeline(
  pipeline: ValidatablePipeline | null | undefined,
  emittedPromptTemplates: EmittedPromptTemplateRef[],
  knownServerTemplates: KnownServerPromptTemplate[] = [],
  profileModels: unknown[] = []
): PipelineValidationIssue[] {
  const issues: PipelineValidationIssue[] = [];
  if (!pipeline || typeof pipeline !== 'object') return issues;

  const checkpoints = asArray(pipeline.checkpoints);
  const outputFormat = asRecord(pipeline.output_format);
  const templateRefs = asArray(emittedPromptTemplates) as EmittedPromptTemplateRef[];

  const emittedIds = new Set<string>();
  // Content by id, for the templates we actually have content for (i.e. were
  // passed as `{id, content}` rather than a bare id string).
  const emittedContentById = new Map<string, string>();
  templateRefs.forEach((ref) => {
    if (typeof ref === 'string') {
      const id = ref.trim();
      if (id) emittedIds.add(id);
      return;
    }
    if (ref && typeof ref === 'object') {
      const id = asString((ref as { id?: unknown }).id).trim();
      if (!id) return;
      emittedIds.add(id);
      emittedContentById.set(id, asString((ref as { content?: unknown }).content));
    }
  });

  // Server-known ids + content, so "wasn't emitted" warnings and generator-
  // template-fitness checks can also see templates that already exist server-side.
  const serverIds = new Set<string>();
  const serverContentById = new Map<string, string>();
  asArray(knownServerTemplates).forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const id = asString((raw as { id?: unknown }).id).trim();
    if (!id) return;
    serverIds.add(id);
    const content = (raw as { content?: unknown }).content;
    if (typeof content === 'string') serverContentById.set(id, content);
  });

  // Profile catalog entries by ref ("<slug>@<version>"), for the profile
  // input-compatibility rule. Empty when the caller has no catalog on hand.
  const profileModelsById = new Map<string, unknown>();
  asArray(profileModels).forEach((model) => {
    const id = asString(asRecord(model).id).trim();
    if (id) profileModelsById.set(id, model);
  });

  validateOutputFormatCompleteness(outputFormat, issues);
  validateClipStyleRepertoire(templateRefs, issues);

  const seenIds = new Set<string>();
  // Distributor checkpoint ids seen so far, in emission order — a connector must
  // reference (or simply follow) an EARLIER distributor.
  const seenDistributorIds = new Set<string>();
  // Ids of checkpoints STRICTLY BEFORE the one being validated — the set a
  // required_assets `checkpoint:<id>` source has to resolve inside (the engine
  // only ever hydrates outputs/attachments of prior checkpoints).
  const priorIds = new Set<string>();

  checkpoints.forEach((raw, index) => {
    const cp = asRecord(raw);
    const id = asString(cp.id).trim();
    const label = checkpointLabel(id, index);
    const checkpointId = id || null;

    if (!id) {
      issues.push({ level: 'error', checkpointId: null, message: `Checkpoint at position ${index + 1} is missing an id.` });
    } else if (seenIds.has(id)) {
      issues.push({ level: 'error', checkpointId: id, message: `Checkpoint id "${id}" is used by more than one checkpoint; ids must be unique.` });
    } else {
      seenIds.add(id);
    }

    const rawType = asString(cp.type).trim();
    const type = rawType || 'prompt'; // missing type defaults to prompt server-side
    if (rawType && !CHECKPOINT_TYPES.has(rawType)) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" has unsupported type "${rawType}" — expected one of prompt, distributor, sequencer, connector, generator, upload, tool, agent.`,
      });
    }

    // A source distributor fans out over an earlier step's shots without a
    // planner call, so it has no prompt to name (the server checks the source).
    const fansOutFromSource = type === 'distributor' && asString(asRecord(cp.distributor).source).trim() !== '';
    if (type === 'prompt' || type === 'distributor' || type === 'sequencer' || type === 'generator') {
      const promptTemplateId = asString(cp.prompt_template_id).trim();
      if (!promptTemplateId) {
        if (!fansOutFromSource) {
          issues.push({
            level: 'error',
            checkpointId,
            message: `Checkpoint "${label}" is missing prompt_template_id — required for prompt, distributor, sequencer, and generator checkpoints.`,
          });
        }
      } else if (
        !emittedIds.has(promptTemplateId) &&
        !KNOWN_BUILTIN_PROMPT_TEMPLATE_IDS.has(promptTemplateId) &&
        !serverIds.has(promptTemplateId)
      ) {
        issues.push({
          level: 'warning',
          checkpointId,
          message: `Checkpoint "${label}" references prompt_template_id "${promptTemplateId}", which wasn't emitted as a prompt-template block in this message — make sure it already exists on the server.`,
        });
      }
    }

    if (type === 'generator') {
      validateGeneratorCheckpoint(cp, label, checkpointId, outputFormat, profileModelsById, issues);
      validateGeneratorTemplateFitness(cp, label, checkpointId, emittedContentById, serverContentById, issues);
    }

    if (type === 'distributor') {
      if (!cp.distributor || typeof cp.distributor !== 'object') {
        issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" is a distributor checkpoint but has no distributor block.` });
      }
      if (id) seenDistributorIds.add(id);
    }

    if (type === 'sequencer') {
      validateSequencerCheckpoint(cp, label, checkpointId, priorIds, issues);
      // A connector collects from a sequencer exactly as it does from a
      // distributor (the sequencer rolls its per-step attachments up onto its
      // own slot), so it joins the same fan-out bookkeeping.
      if (id) seenDistributorIds.add(id);
    }

    if (type === 'connector') {
      validateConnectorCheckpoint(cp, label, checkpointId, seenDistributorIds, issues);
    }

    if (type === 'upload') {
      validateUploadCheckpoint(cp, label, checkpointId, issues);
    }

    if (type === 'tool') {
      validateToolCheckpoint(cp, label, checkpointId, issues);
    }

    if (type === 'agent') {
      validateAgentCheckpoint(cp, label, checkpointId, issues);
    }

    validateRequiredAssets(cp, type, label, checkpointId, priorIds, issues);
    validateSkipWhen(cp, label, checkpointId, priorIds, issues);
    validateReviewCoreFields(cp, type, label, checkpointId, issues);
    validateReviewBlock(cp.review, `Checkpoint "${label}" review`, issues, {
      checkpointId,
      pairJudgeSatisfied: type === 'generator' && asRecord(cp.generator).closing_frame === true,
    });
    validateOutputContract(cp, type, label, checkpointId, issues);
    validateOutputFields(cp, type, label, checkpointId, issues);

    if (id) priorIds.add(id);
  });

  validateParameterUsage(pipeline, checkpoints, issues);
  validateReviewBlock(pipeline.review_defaults, 'review_defaults', issues, {
    checkpointId: null,
    // Never warned regardless of what checkpoints exist — pair_judge in
    // review_defaults/account capabilities is a cascade default, not a
    // specific checkpoint's setting.
    pairJudgeSatisfied: true,
  });

  return issues;
}

/**
 * required_assets have exactly ONE supported wiring: `source: "checkpoint:<id>"`
 * naming a PRIOR checkpoint that provides the asset. Mirrors
 * validateRequiredAssetsConfig + normalizeRequiredAssets in validation.go:
 *
 *   - a legacy `source` ("user", "any", "initial", "upload", "") is rejected —
 *     at runtime a bare `user` only ever matches attachments POSTed onto this
 *     very checkpoint, so an automatic run pauses forever (the food-horror
 *     deadlock). When the row still carries a `checkpoint_id`, the message names
 *     the exact replacement the save-time normalizer would have written.
 *   - the referenced checkpoint must exist EARLIER in the pipeline.
 *   - required_assets on a non-generator checkpoint are stripped at save (only
 *     generators consume them), so they are a warning, not an error.
 */
function validateRequiredAssets(
  cp: Record<string, unknown>,
  type: string,
  label: string,
  checkpointId: string | null,
  priorIds: Set<string>,
  issues: PipelineValidationIssue[]
): void {
  const requiredAssets = asArray(cp.required_assets);
  if (requiredAssets.length === 0) return;

  if (type !== 'generator') {
    issues.push({
      level: 'warning',
      checkpointId,
      message: `Checkpoint "${label}" is a ${type} checkpoint with ${requiredAssets.length} required_assets — they are stripped at save: only generator checkpoints consume required assets, on any other type a missing entry just deadlocks the run.`,
    });
    return;
  }

  requiredAssets.forEach((rawAsset, assetIndex) => {
    const asset = asRecord(rawAsset);
    const source = asString(asset.source).trim();
    const legacyCheckpointId = asString(asset.checkpoint_id).trim();

    if (!source.toLowerCase().startsWith(CHECKPOINT_SOURCE_PREFIX)) {
      const suggestion = legacyCheckpointId
        ? `use source:"${CHECKPOINT_SOURCE_PREFIX}${legacyCheckpointId}"`
        : 'name the checkpoint that provides the asset with source:"checkpoint:<id>"';
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" required_assets[${assetIndex}].source ${
          source ? `"${source}"` : 'is missing'
        } — the only supported source is checkpoint:<id>; ${suggestion}.`,
      });
      return;
    }

    const sourceCheckpointId = source.slice(CHECKPOINT_SOURCE_PREFIX.length).trim();
    if (!sourceCheckpointId) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" required_assets[${assetIndex}].source "${source}" names no checkpoint — expected checkpoint:<id>.`,
      });
      return;
    }
    if (!priorIds.has(sourceCheckpointId)) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" required_assets[${assetIndex}].source "${source}" must reference an EARLIER checkpoint — "${sourceCheckpointId}" is not one of the checkpoints before it.`,
      });
    }
  });
}

/**
 * skip_when's checkpoint form (mirrors Go validation.validateSkipWhen): one
 * form per rule, and "<id>[.<field>]" names a checkpoint BEFORE this one — the
 * engine evaluates skip_when before the step runs, so a later, unknown or own
 * checkpoint never has an output to read. The param form is checked by
 * validateParameterUsage.
 */
function validateSkipWhen(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  priorIds: Set<string>,
  issues: PipelineValidationIssue[]
): void {
  const skipWhen = asRecord(cp.skip_when);
  const ref = asString(skipWhen.checkpoint).trim();
  if (!ref) return;
  if (asString(skipWhen.param).trim()) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" skip_when sets both param and checkpoint — use one.` });
    return;
  }
  const parts = ref.split('.');
  if (parts.some((part) => !part.trim() || part.includes(':'))) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" skip_when.checkpoint "${ref}" must look like <checkpoint id>[.<field>] (e.g. brief.source).`,
    });
    return;
  }
  if (!priorIds.has(parts[0])) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" skip_when.checkpoint "${ref}" must name an EARLIER checkpoint — "${parts[0]}" is not one of the checkpoints before it.`,
    });
  }
}

/** One `param:<name>` reference, with a human-readable location for the message. */
interface ParameterReference {
  name: string;
  where: string;
  checkpointId: string | null;
}

/** Collects every place a template reads a run parameter (mirrors collectParameterReferences). */
function collectParameterReferences(checkpoints: unknown[]): ParameterReference[] {
  const refs: ParameterReference[] = [];

  checkpoints.forEach((raw, index) => {
    const cp = asRecord(raw);
    const id = asString(cp.id).trim();
    const label = checkpointLabel(id, index);
    const checkpointId = id || null;

    const inputMapping = asRecord(cp.input_mapping);
    Object.keys(inputMapping)
      .sort() // deterministic message ordering, like the Go validator
      .forEach((placeholder) => {
        const source = asString(inputMapping[placeholder]).trim();
        if (!source.startsWith('param:')) return;
        const name = source.slice('param:'.length).trim();
        if (name) refs.push({ name, where: `Checkpoint "${label}" input_mapping["${placeholder}"]`, checkpointId });
      });

    const maxChildrenParam = asString(asRecord(cp.distributor).max_children_param).trim();
    if (maxChildrenParam) {
      refs.push({ name: maxChildrenParam, where: `Checkpoint "${label}" distributor.max_children_param`, checkpointId });
    }

    const maxStepsParam = asString(asRecord(cp.sequencer).max_steps_param).trim();
    if (maxStepsParam) {
      refs.push({ name: maxStepsParam, where: `Checkpoint "${label}" sequencer.max_steps_param`, checkpointId });
    }

    const skipWhenParam = asString(asRecord(cp.skip_when).param).trim();
    if (skipWhenParam) {
      refs.push({ name: skipWhenParam, where: `Checkpoint "${label}" skip_when.param`, checkpointId });
    }
  });

  return refs;
}

/**
 * Cross-checks declared parameters against the places that read them (mirrors
 * ValidateParameterUsage in validation.go). BOTH directions are warnings:
 * declaring a parameter is optional, so referenced-but-undeclared just means the
 * value comes from the run (or, unset, renders "" / falls back to the literal
 * max_children cap), and declared-but-unreferenced is inert config.
 */
function validateParameterUsage(
  pipeline: ValidatablePipeline,
  checkpoints: unknown[],
  issues: PipelineValidationIssue[]
): void {
  const declared = new Set(Object.keys(asRecord(pipeline.parameters)));
  const referenced = new Set<string>();

  collectParameterReferences(checkpoints).forEach((ref) => {
    referenced.add(ref.name);
    if (declared.has(ref.name)) return;
    issues.push({
      level: 'warning',
      checkpointId: ref.checkpointId,
      code: 'undeclared_parameter',
      message: `${ref.where} references parameter "${ref.name}", which isn't declared in "parameters" — declaring it (with a default) is optional; unset, prompts render "" and max_children falls back to the literal cap.`,
    });
  });

  Array.from(declared)
    .sort()
    .forEach((name) => {
      if (referenced.has(name)) return;
      issues.push({
        level: 'warning',
        checkpointId: null,
        message: `Parameter "${name}" is declared but never referenced (no "param:${name}" input_mapping, distributor.max_children_param, sequencer.max_steps_param, or skip_when) — it has no effect.`,
      });
    });
}

/** Provider inferred for a model, plus the label/example used in the pairing-suggestion message. */
interface InferredProvider {
  provider: string;
  /** Label used in the "<label> is a model served by <provider>" clause. */
  label: string;
  /** Example value suggested for the paired `<media>_model` field. */
  exampleModel: string;
}

/**
 * Infers the provider that serves `model`, from well-known model-id prefixes/families.
 * Mirrors the pairing hints the server can offer at save time. Returns null when the
 * model doesn't match any known family, i.e. the client has no suggestion to offer.
 */
function inferProviderForModel(model: string): InferredProvider | null {
  const m = model.trim();
  if (/^vidu:/i.test(m)) return { provider: 'runware', label: 'vidu', exampleModel: m };
  if (/^vidu$/i.test(m)) return { provider: 'runware', label: 'vidu', exampleModel: 'vidu:4@2' };
  if (/^veo-/i.test(m)) return { provider: 'vertexai', label: m, exampleModel: m };
  if (/^gemini/i.test(m) && /image/i.test(m)) return { provider: 'vertexai', label: m, exampleModel: m };
  if (/^v[45]/i.test(m)) return { provider: 'suno', label: m, exampleModel: m };
  if (/^(google|openai|runware|klingai|elevenlabs|minimax|xai):/i.test(m)) {
    return { provider: 'runware', label: m, exampleModel: m };
  }
  return null;
}

/**
 * Validates pipeline.output_format: every media type (image, video, audio) needs
 * BOTH a `<media>_provider` and `<media>_model`. Mirrors the Go backend's strict
 * save-time requirement — all three modality pairs must be fully specified.
 */
function validateOutputFormatCompleteness(outputFormat: Record<string, unknown>, issues: PipelineValidationIssue[]): void {
  MEDIA_TYPES.forEach((mediaType) => {
    const provider = asString(outputFormat[`${mediaType}_provider`]).trim();
    const model = asString(outputFormat[`${mediaType}_model`]).trim();

    if (!provider && !model) {
      const rec = RECOMMENDED_OUTPUT_FORMAT_DEFAULTS[mediaType];
      issues.push({
        level: 'error',
        checkpointId: null,
        message: `output_format has no ${mediaType} provider/model — recommended default: ${rec.provider} / ${rec.model}`,
        code: 'output_format',
      });
      return;
    }

    if (model && !provider) {
      const inferred = inferProviderForModel(model);
      if (inferred) {
        issues.push({
          level: 'error',
          checkpointId: null,
          message: `output_format.${mediaType}_model "${model}" needs a provider — ${inferred.label} is a model served by ${inferred.provider} (use ${mediaType}_provider "${inferred.provider}", ${mediaType}_model "${inferred.exampleModel}")`,
          code: 'output_format',
        });
      } else {
        issues.push({
          level: 'error',
          checkpointId: null,
          message: `output_format.${mediaType}_model "${model}" is set but output_format.${mediaType}_provider is missing.`,
          code: 'output_format',
        });
      }
      return;
    }

    if (provider && !model) {
      issues.push({
        level: 'error',
        checkpointId: null,
        message: `output_format.${mediaType}_provider "${provider}" is set but output_format.${mediaType}_model is missing.`,
        code: 'output_format',
      });
    }
  });
}

/** Unique, trimmed `{{placeholder}}` names found in `content`, in first-seen order.
 * Exported for the canvas graph derivation (suggested input_mapping keys). */
export function extractPlaceholders(content: string): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const regex = /\{\{\s*([^{}]+?)\s*\}\}/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(content)) !== null) {
    const name = match[1].trim();
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

/**
 * Checks a generator checkpoint's referenced prompt template (when its content is
 * known — either emitted in this message, the generator-pass-through built-in, or
 * a known server-side template) for fitness as a *generator* prompt: every
 * placeholder must be mapped in input_mapping, at least one placeholder must
 * actually be mapped, and it must not look like an assembly template
 * (aiVideoPrompts/imagePrompts/clipStyle).
 */
function validateGeneratorTemplateFitness(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  emittedContentById: Map<string, string>,
  serverContentById: Map<string, string>,
  issues: PipelineValidationIssue[]
): void {
  const promptTemplateId = asString(cp.prompt_template_id).trim();
  if (!promptTemplateId) return;

  let content: string | null = null;
  if (promptTemplateId === 'generator-pass-through-prompt') {
    content = PASS_THROUGH_TEMPLATE_CONTENT;
  } else if (emittedContentById.has(promptTemplateId)) {
    content = emittedContentById.get(promptTemplateId) ?? '';
  } else if (serverContentById.has(promptTemplateId)) {
    // Not emitted in this message, but the server already has this template and
    // we know its content — check it the same way so a generator checkpoint
    // mistakenly pointed at a server-side assembly template is still caught.
    content = serverContentById.get(promptTemplateId) ?? '';
  }
  if (content === null) return; // no content available anywhere — nothing to check

  const marker = ASSEMBLY_TEMPLATE_MARKERS.find((needle) => content!.includes(needle));
  if (marker) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" prompt template "${promptTemplateId}" looks like an assembly template (contains "${marker}"), not a generator prompt — generator checkpoints need a plain prompt template.`,
    });
  }

  const placeholders = extractPlaceholders(content);
  const inputMappingKeys = new Set(Object.keys(asRecord(cp.input_mapping)));
  placeholders.forEach((placeholder) => {
    if (!inputMappingKeys.has(placeholder)) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" prompt template "${promptTemplateId}" references {{${placeholder}}}, which isn't a key in generator.input_mapping.`,
      });
    }
  });
  // Fully static content (zero placeholders) is a legitimate fixed-prompt
  // pattern the server accepts (e.g. a constant backdrop generator) — surface
  // it as a warning so an unintentional static template is still noticed.
  if (placeholders.length === 0) {
    issues.push({
      level: 'warning',
      checkpointId,
      message: `Checkpoint "${label}" prompt template "${promptTemplateId}" contains no {{placeholders}} — the same fixed prompt will be used for every generation. Intentional?`,
    });
  }
}

/**
 * Checks every emitted prompt template whose content contains a literal "clipStyle"
 * key for values the renderer doesn't actually support.
 */
function validateClipStyleRepertoire(templateRefs: EmittedPromptTemplateRef[], issues: PipelineValidationIssue[]): void {
  const CLIP_STYLE_REGEX = /"clipStyle"\s*:\s*"([^"]+)"/g;

  templateRefs.forEach((ref) => {
    if (typeof ref === 'string' || !ref || typeof ref !== 'object') return;
    const content = asString((ref as { content?: unknown }).content);
    if (!content || !content.includes('clipStyle')) return;

    const seenInvalid = new Set<string>();
    CLIP_STYLE_REGEX.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = CLIP_STYLE_REGEX.exec(content)) !== null) {
      const value = match[1];
      if (SUPPORTED_CLIP_STYLES.has(value) || seenInvalid.has(value)) continue;
      seenInvalid.add(value);
      issues.push({
        level: 'error',
        checkpointId: null,
        message: `clipStyle "${value}" cannot be rendered — only "reel", "quote" and "genericCarousel" are supported by the renderer`,
      });
    }
  });
}

function validateGeneratorCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  outputFormat: Record<string, unknown>,
  profileModelsById: Map<string, unknown>,
  issues: PipelineValidationIssue[]
): void {
  const generator = cp.generator;
  if (!generator || typeof generator !== 'object' || Array.isArray(generator)) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" is a generator checkpoint but has no generator block.` });
  } else {
    const generatorRecord = generator as Record<string, unknown>;
    const mediaType = asString(generatorRecord.media_type).trim().toLowerCase();
    if (!mediaType || !MEDIA_TYPES.has(mediaType)) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" generator.media_type must be one of image, video, audio (got "${asString(generatorRecord.media_type) || 'nothing'}").`,
      });
    } else {
      const provider = asString(generatorRecord.provider).trim();
      const model = asString(generatorRecord.model).trim();
      const fallbackProvider = asString(outputFormat[`${mediaType}_provider`]).trim();
      const fallbackModel = asString(outputFormat[`${mediaType}_model`]).trim();
      const effectiveProvider = provider || fallbackProvider;
      const effectiveModel = model || fallbackModel;

      if (!effectiveProvider) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" is missing generator.provider — set it on the checkpoint or add output_format.${mediaType}_provider.`,
        });
      }
      if (!effectiveModel) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" is missing generator.model — set it on the checkpoint or add output_format.${mediaType}_model.`,
        });
      }

      if (effectiveProvider) {
        const providerLower = effectiveProvider.toLowerCase();
        const allowed = PROVIDER_ALLOW_LIST[mediaType] || [];
        if (providerLower === 'profile' && mediaType !== 'image') {
          issues.push({
            level: 'error',
            checkpointId,
            message: `Checkpoint "${label}" uses provider "profile", which only supports media_type "image" (got "${mediaType}").`,
          });
        } else if (!allowed.includes(providerLower)) {
          issues.push({
            level: 'error',
            checkpointId,
            message: `Checkpoint "${label}" generator.provider "${effectiveProvider}" isn't valid for media_type "${mediaType}" (allowed: ${allowed.join(', ')}).`,
          });
        }

        if (providerLower === 'profile' && effectiveModel && !isValidProfileModelRef(effectiveModel)) {
          issues.push({
            level: 'error',
            checkpointId,
            message: `Checkpoint "${label}" uses provider "profile", whose model must look like "<slug>@<version>" (got "${effectiveModel}").`,
          });
        } else if (providerLower === 'profile' && effectiveModel && mediaType === 'image') {
          // Gated on media_type too: a video/audio "profile" generator is already
          // rejected above, and piling a second finding on the same mistake reads
          // as two problems instead of one.
          validateProfileInputCompatibility(cp, label, checkpointId, effectiveModel, profileModelsById, issues);
        }
      }
    }
  }

  validateImageToImageReferenceSource(cp, label, checkpointId, issues);

  const requiresConfirm = cp.requires_confirm === true;
  const requiredAssets = asArray(cp.required_assets);
  if (requiresConfirm && requiredAssets.length > 0) return; // explicitly exempted from the prompt-source check

  const inputMapping = asRecord(cp.input_mapping);
  // `param:` is deliberately NOT a prompt source: the Go validator only accepts
  // initial_input or checkpoint:<id>[.field] (validation.go). A generator whose
  // only "prompt" is a run parameter renders an empty prompt whenever the run
  // doesn't supply one.
  const hasPromptSource = Object.values(inputMapping).some((value) => {
    if (typeof value !== 'string') return false;
    const source = value.trim();
    return source === 'initial_input' || source.startsWith('checkpoint:');
  });
  if (!hasPromptSource) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" generator.input_mapping must include a prompt source (a value equal to "initial_input" or starting with "checkpoint:"), or set requires_confirm with required_assets.`,
    });
  }
}

/**
 * An img2img/inpaint generator profile resolves its seed image (and, for
 * inpaint, its "mask" attachment) solely from the checkpoint's run-time
 * attachments, which only required_assets and "attachments:<id>" input_mapping
 * sources can supply. Mirrors ValidateProfileGeneratorAssets in the Go backend,
 * including its deliberate asymmetry: a missing attachment source is a hard
 * error (guaranteed to fail the run), while a missing mask DECLARATION is only a
 * warning — a mask arrives by attachment role, which a declaration cannot
 * disprove.
 *
 * Silent when the profile isn't in the supplied catalog (nothing to judge
 * against); a warning when it is listed but carries no compatibility metadata
 * (a registry that predates the fields).
 */
function validateProfileInputCompatibility(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  modelRef: string,
  profileModelsById: Map<string, unknown>,
  issues: PipelineValidationIssue[]
): void {
  const model = profileModelsById.get(modelRef);
  if (model === undefined) return; // no catalog, or a ref it doesn't list — nothing to judge

  const compatibility = readProfileCompatibility(model);
  if (!compatibility) {
    issues.push({
      level: 'warning',
      checkpointId,
      message: `Checkpoint "${label}" uses generator profile "${modelRef}", whose input requirements couldn't be verified (the model registry reports no generation mode for it) — if it is an img2img/inpaint profile it will need a seed image at run time.`,
    });
    return;
  }
  if (!compatibility.requiresSeedImage) return;

  if (!checkpointDeclaresAttachmentSource(cp)) {
    issues.push({
      level: 'error',
      checkpointId,
      code: 'profile_seed_image',
      message: `Checkpoint "${label}" uses generator profile "${modelRef}", which runs in "${compatibility.generationMode}" mode and needs a seed image, but no image source is declared — add a required_assets entry naming the image (or an input_mapping "attachments:<checkpoint_id>" source); a profile generator reads its seed image only from the checkpoint's attachments.`,
    });
    return;
  }

  if (compatibility.requiresMask && !checkpointDeclaresMaskSource(cp)) {
    issues.push({
      level: 'warning',
      checkpointId,
      message: `Checkpoint "${label}" uses generator profile "${modelRef}", which runs in "inpaint" mode and additionally needs an attachment with role "mask" at run time, but no mask source is declared — add a required_assets entry with key "mask" (or an input_mapping "attachments:<checkpoint_id>?role=mask" source), or attach the mask when the run pauses.`,
    });
  }
}

/**
 * An image_to_image generator only ever receives the reference images it
 * EXPLICITLY sources: a required_assets entry, an `attachments:<checkpoint_id>`
 * input_mapping source, or a literal seed/reference in output_spec. With none of
 * them it silently produces a text-to-image result (mirrors
 * validateImageToImageReferenceSource in validation.go).
 */
function validateImageToImageReferenceSource(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const mode = asString(asRecord(cp.generator).mode).trim();
  if (mode !== 'image_to_image') return;

  if (asArray(cp.required_assets).length > 0) return;
  const usesAttachmentSource = Object.values(asRecord(cp.input_mapping)).some(
    (value) => typeof value === 'string' && value.trim().startsWith('attachments:')
  );
  if (usesAttachmentSource) return;
  if (outputSpecDeclaresReferenceInput(cp.output_spec)) return;

  issues.push({
    level: 'error',
    checkpointId,
    code: 'image_to_image_reference',
    message: `Checkpoint "${label}" generator.mode is "image_to_image" but no reference source is declared — add a required_assets entry naming the reference (or an input_mapping "attachments:<checkpoint_id>" source); otherwise the generator receives no reference image and silently produces a text-to-image result.`,
  });
}

/** True when an output_spec carries a literal seed/reference entry, flat or nested under image/ai_video. */
function outputSpecDeclaresReferenceInput(rawOutputSpec: unknown): boolean {
  const outputSpec = asRecord(rawOutputSpec);
  const hasRef = (record: Record<string, unknown>): boolean =>
    OUTPUT_SPEC_REFERENCE_KEYS.some((key) => record[key] != null);

  if (hasRef(outputSpec)) return true;
  return ['image', 'ai_video'].some((nestedKey) => hasRef(asRecord(outputSpec[nestedKey])));
}

/**
 * Validates a `sequencer` block (Go pipeline.SequencerConfig). Mirrors the
 * engine rules: the block is mandatory, `max_steps` is the hard cap on how many
 * steps the planner may emit (so it must be at least 1), and every
 * `allowed_parent_refs` value names either `user` or a checkpoint EARLIER in the
 * pipeline — a step can only ever be handed something already produced.
 */
function validateSequencerCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  priorIds: Set<string>,
  issues: PipelineValidationIssue[]
): void {
  const sequencer = cp.sequencer;
  if (!sequencer || typeof sequencer !== 'object' || Array.isArray(sequencer)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" is a sequencer checkpoint but has no sequencer block.`,
    });
    return;
  }

  const record = sequencer as Record<string, unknown>;
  const maxSteps = typeof record.max_steps === 'number' ? record.max_steps : Number(record.max_steps);
  if (!Number.isFinite(maxSteps) || maxSteps < 1) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" sequencer.max_steps must be at least 1 (got ${
        record.max_steps === undefined ? 'nothing' : JSON.stringify(record.max_steps)
      }).`,
    });
  }

  const refs = asRecord(record.allowed_parent_refs);
  Object.keys(refs)
    .sort()
    .forEach((name) => {
      const source = asString(refs[name]).trim();
      if (!source) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" sequencer.allowed_parent_refs["${name}"] has no source — expected checkpoint:<id> or "user".`,
        });
        return;
      }
      if (source.toLowerCase() === USER_REF_SOURCE) return;
      if (!source.toLowerCase().startsWith(CHECKPOINT_SOURCE_PREFIX)) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" sequencer.allowed_parent_refs["${name}"] source "${source}" is not supported — expected checkpoint:<id> or "user".`,
        });
        return;
      }
      const sourceCheckpointId = source.slice(CHECKPOINT_SOURCE_PREFIX.length).trim();
      if (!sourceCheckpointId) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" sequencer.allowed_parent_refs["${name}"] source "${source}" names no checkpoint — expected checkpoint:<id>.`,
        });
        return;
      }
      if (!priorIds.has(sourceCheckpointId)) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" sequencer.allowed_parent_refs["${name}"] source "${source}" must reference an EARLIER checkpoint — "${sourceCheckpointId}" is not one of the checkpoints before it.`,
        });
      }
    });
}

/**
 * Validates the core fields of an optional `review` block (Go
 * pipeline.ReviewConfig), NOT the ten capability fields — see
 * {@link validateReviewBlock} below for those. Mirrors the engine rules
 * exactly, as ERRORS: the engine only runs a verdict loop on prompt/generator
 * checkpoints, the judge needs `context.criteria` to judge anything at all,
 * regeneration is bounded at 3 attempts, the confidence gate is a
 * probability, and skip_when_empty sits on prompt checkpoints only and names
 * context keys.
 */
function validateReviewCoreFields(
  cp: Record<string, unknown>,
  type: string,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const review = cp.review;
  if (review === undefined || review === null) return;

  if (typeof review !== 'object' || Array.isArray(review)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" has a review value that is not an object.`,
    });
    return;
  }

  if (!REVIEWABLE_TYPES.has(type)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" is a ${type} checkpoint with a review block — review is only supported on prompt and generator checkpoints.`,
    });
    return;
  }

  const record = review as Record<string, unknown>;
  const context = asRecord(record.context);
  if (!asString(context.criteria).trim()) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" review.context must contain "criteria" — the judge has nothing to check without it.`,
    });
  }

  // skip_when_empty (AV-2/AV-4): only the prompt review reads it, and every key
  // it names must be a key of review.context (Go validation/review.go).
  if (Array.isArray(record.skip_when_empty) && record.skip_when_empty.length > 0) {
    if (type !== 'prompt') {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" review.skip_when_empty is only supported on prompt checkpoints (this one is a ${type} checkpoint) — its other judges would still run and bill.`,
      });
    }
    record.skip_when_empty.forEach((key) => {
      if (typeof key !== 'string' || !key.trim() || !(key in context)) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `Checkpoint "${label}" review.skip_when_empty names ${JSON.stringify(key)}, which review.context does not define.`,
        });
      }
    });
  }

  if (record.max_attempts !== undefined) {
    const maxAttempts =
      typeof record.max_attempts === 'number' ? record.max_attempts : Number(record.max_attempts);
    if (!Number.isFinite(maxAttempts) || maxAttempts < 0 || maxAttempts > 3) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" review.max_attempts must be between 0 and 3 (got ${JSON.stringify(
          record.max_attempts
        )}).`,
      });
    }
  }

  if (record.confidence_gate !== undefined) {
    const gate =
      typeof record.confidence_gate === 'number'
        ? record.confidence_gate
        : Number(record.confidence_gate);
    if (!Number.isFinite(gate) || gate < 0 || gate > 1) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" review.confidence_gate must be between 0 and 1 (got ${JSON.stringify(
          record.confidence_gate
        )}).`,
      });
    }
  }
}

/** Case-insensitive membership check against {@link REVIEW_LENSES}. */
const REVIEW_LENS_SET = new Set<string>(REVIEW_LENSES);

/** Options for {@link validateReviewBlock}, distinguishing a per-checkpoint
 *  call from the template-level `review_defaults` call. */
interface ReviewCapabilityLintOptions {
  checkpointId: string | null;
  /**
   * Whether a `pair_judge: true` here needs no warning. True for a generator
   * checkpoint that also declares `generator.closing_frame: true` (the only
   * checkpoint shape the engine actually runs a pair judge on), and ALWAYS
   * true for `template.review_defaults` / account capabilities — those are
   * cascade defaults, not a specific checkpoint, and per the ruling are never
   * warned about regardless of what checkpoints exist.
   */
  pairJudgeSatisfied: boolean;
}

/**
 * Validates the ten review-capability fields (Go types.ReviewCapabilities,
 * flattened into both `ReviewConfig` and `ReviewDefaults`): `story`,
 * `scene_scope`, `descriptions`, `attempt_history`, `cast`, `pair_judge`,
 * `pair`, `fixed_checks`, `lenses`, `guidance`. Applied to every checkpoint's
 * `review` AND to `template.review_defaults` — the same ten fields, the same
 * rules,
 * so a caller need not special-case which layer it's looking at. Mirrors
 * `types.ReviewCapabilities.Validate` as ERRORS, except `pair_judge` without
 * its precondition, which is a WARNING (the engine still runs the checkpoint,
 * just without a second judge) and is never blocking.
 *
 * @param review the `review` (or `review_defaults`) value, unknown/loose like
 *   the rest of this file since it may be mid-typed LLM-emitted JSON.
 * @param where the subject phrase messages are prefixed with — e.g.
 *   `Checkpoint "cp-1" review` or `review_defaults`.
 */
function validateReviewBlock(
  review: unknown,
  where: string,
  issues: PipelineValidationIssue[],
  opts: ReviewCapabilityLintOptions
): void {
  if (review === undefined || review === null) return;
  if (typeof review !== 'object' || Array.isArray(review)) return; // reported by validateReviewCoreFields

  const record = review as Record<string, unknown>;
  const { checkpointId } = opts;

  if (record.scene_scope !== undefined) {
    const sceneScope =
      typeof record.scene_scope === 'number' ? record.scene_scope : Number(record.scene_scope);
    if (!Number.isInteger(sceneScope) || sceneScope < 0 || sceneScope > 3) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `${where}.scene_scope must be an integer between 0 and 3 (got ${JSON.stringify(
          record.scene_scope
        )}).`,
      });
    }
  }

  if (record.lenses !== undefined) {
    const seen = new Set<string>();
    asArray(record.lenses).forEach((raw) => {
      const lens = asString(raw).trim().toLowerCase();
      if (!lens || !REVIEW_LENS_SET.has(lens)) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `${where}.lenses has an unsupported entry "${asString(raw)}" — expected one of ${REVIEW_LENSES.join(
            ', '
          )}.`,
        });
        return;
      }
      if (seen.has(lens)) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `${where}.lenses has "${lens}" more than once — duplicates aren't allowed.`,
        });
        return;
      }
      seen.add(lens);
    });
  }

  if (record.fixed_checks !== undefined) {
    asArray(record.fixed_checks).forEach((raw, index) => {
      const text = asString(asRecord(raw).check).trim();
      if (!text) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `${where}.fixed_checks[${index}].check must not be empty.`,
        });
      } else if (text.length > 300) {
        issues.push({
          level: 'error',
          checkpointId,
          message: `${where}.fixed_checks[${index}].check must be 300 characters or fewer (got ${text.length}).`,
        });
      }
    });
  }

  if (typeof record.guidance === 'string' && record.guidance.length > 2000) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `${where}.guidance must be 2000 characters or fewer (got ${record.guidance.length}).`,
    });
  }

  if (record.pair_judge === true && !opts.pairJudgeSatisfied) {
    issues.push({
      level: 'warning',
      checkpointId,
      message: `${where}.pair_judge is set to true, but this checkpoint is not a generator with closing_frame: true — pair judging only runs there, so this checkpoint will run without it.`,
    });
  }
}

function validateConnectorCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  seenDistributorIds: Set<string>,
  issues: PipelineValidationIssue[]
): void {
  const connector = cp.connector;
  if (!connector || typeof connector !== 'object' || Array.isArray(connector)) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" is a connector checkpoint but has no connector block.` });
    return;
  }

  const connectorRecord = connector as Record<string, unknown>;
  const strategy = asString(connectorRecord.strategy).trim();
  if (!strategy || !CONNECTOR_STRATEGIES.has(strategy)) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" connector.strategy must be one of collect_all, collect_lora_dataset (got "${strategy || 'nothing'}").`,
    });
  }

  const sourceCheckpointId = asString(connectorRecord.source_checkpoint_id).trim();
  if (sourceCheckpointId) {
    if (!seenDistributorIds.has(sourceCheckpointId)) {
      issues.push({
        level: 'error',
        checkpointId,
        message: `Checkpoint "${label}" connector.source_checkpoint_id "${sourceCheckpointId}" must reference an earlier distributor checkpoint or sequencer.`,
      });
    }
  } else if (seenDistributorIds.size === 0) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" is a connector checkpoint but no distributor checkpoint precedes it (a sequencer counts as one).`,
    });
  }
}

function validateUploadCheckpoint(
  cp: Record<string, unknown>,
  label: string,
  checkpointId: string | null,
  issues: PipelineValidationIssue[]
): void {
  const upload = cp.upload;
  if (!upload || typeof upload !== 'object' || Array.isArray(upload)) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" is an upload checkpoint but has no upload block.` });
    return;
  }

  const uploadRecord = upload as Record<string, unknown>;
  const requiredCount = typeof uploadRecord.required_count === 'number' ? uploadRecord.required_count : 0;
  const maxCount = typeof uploadRecord.max_count === 'number' ? uploadRecord.max_count : 0;

  if (requiredCount < 0) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" upload.required_count cannot be negative.` });
  }
  if (maxCount < 0) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" upload.max_count cannot be negative.` });
  }
  if (requiredCount > 0 && maxCount > 0 && maxCount < requiredCount) {
    issues.push({ level: 'error', checkpointId, message: `Checkpoint "${label}" upload.max_count cannot be less than upload.required_count.` });
  }

  const castName = typeof uploadRecord.cast_name === 'string' ? uploadRecord.cast_name.trim() : '';
  if (Array.from(castName).length > 80) {
    issues.push({
      level: 'error',
      checkpointId,
      message: `Checkpoint "${label}" upload.cast_name must be at most 80 characters — it is one character's name, not a description.`,
    });
  }
}

/** True when `model` is a well-formed "<slug>@<version>" ref (mirrors Go's ParseGeneratorProfileRef). */
function isValidProfileModelRef(model: string): boolean {
  const parts = model.split('@');
  if (parts.length !== 2) return false;
  const [slug, version] = parts.map((part) => part.trim());
  return slug !== '' && /^\d+$/.test(version);
}
